import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, UserRound } from "lucide-react";
import GradientButton from "../../components/GradientButton/GradientButton";
import authService from "../../services/authService";
import "./SignUp.css";

const initialForm = {
  nombre1: "",
  nombre2: "",
  apellido1: "",
  apellido2: "",
  birthDate: "",
  email: "",
  phoneNumber: "",
  password: "",
  confirmPassword: "",
};

const passwordRules = [
  { label: "Mínimo 8 caracteres", test: (value) => value.length >= 8 },
  { label: "Una mayúscula", test: (value) => /[A-Z]/.test(value) },
  { label: "Una minúscula", test: (value) => /[a-z]/.test(value) },
  { label: "Un número", test: (value) => /\d/.test(value) },
  { label: "Un carácter especial", test: (value) => /[^A-Za-z0-9\s]/.test(value) },
];

const normalizeUsernamePart = (value) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getGeneratedUsername = (firstName, firstSurname) => {
  const normalizedName = normalizeUsernamePart(firstName);
  const normalizedSurname = normalizeUsernamePart(firstSurname);
  return normalizedName && normalizedSurname
    ? `${normalizedName}${normalizedSurname.charAt(0)}`
    : "";
};

const SignUpForm = () => {
  const [form, setForm] = useState(initialForm);
  const [visibleFields, setVisibleFields] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const username = useMemo(
    () => getGeneratedUsername(form.nombre1, form.apellido1),
    [form.nombre1, form.apellido1]
  );
  const isPasswordValid = passwordRules.every(({ test }) => test(form.password));
  const passwordsMatch = form.confirmPassword.length > 0 && form.password === form.confirmPassword;
  const currentDate = new Date();
  const today = [
    currentDate.getFullYear(),
    String(currentDate.getMonth() + 1).padStart(2, "0"),
    String(currentDate.getDate()).padStart(2, "0"),
  ].join("-");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setError("");
  };

  const toggleVisibility = (field) => {
    setVisibleFields((previous) => ({ ...previous, [field]: !previous[field] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!username) {
      setError("Ingresa el nombre y el primer apellido para generar tu usuario.");
      return;
    }

    if (!isPasswordValid) {
      setError("La contraseña no cumple con todos los requisitos de seguridad.");
      return;
    }

    if (!passwordsMatch) {
      setError("La contraseña y su confirmación no coinciden.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await authService.register({
        nombre1: form.nombre1.trim(),
        nombre2: form.nombre2.trim(),
        apellido1: form.apellido1.trim(),
        apellido2: form.apellido2.trim(),
        birthDate: form.birthDate,
        username,
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
        password: form.password,
        confirmPassword: form.confirmPassword,
      });
      setSuccess(response.message || "Tu cuenta fue creada correctamente.");
      setForm(initialForm);
    } catch (requestError) {
      const responseData = requestError.response?.data;
      const backendMessage =
        responseData?.errors?.[0] ||
        responseData?.message ||
        "No fue posible crear la cuenta. Intenta nuevamente.";
      setError(typeof backendMessage === "string" ? backendMessage : "No fue posible crear la cuenta.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTextField = (name, label, options = {}) => (
    <div className="signup-field">
      <label htmlFor={name}>
        {label}{options.required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={options.type || "text"}
        value={form[name]}
        onChange={handleChange}
        required={options.required}
        max={options.max}
        autoComplete={options.autoComplete}
        disabled={isSubmitting || Boolean(success)}
      />
    </div>
  );

  const renderPasswordField = (name, label, autoComplete) => (
    <div className="signup-field">
      <label htmlFor={name}>{label} *</label>
      <div className="signup-password-input">
        <input
          id={name}
          name={name}
          type={visibleFields[name] ? "text" : "password"}
          value={form[name]}
          onChange={handleChange}
          autoComplete={autoComplete}
          required
          disabled={isSubmitting || Boolean(success)}
        />
        <button
          type="button"
          onClick={() => toggleVisibility(name)}
          aria-label={visibleFields[name] ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}
          disabled={isSubmitting}
        >
          {visibleFields[name] ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <section className="signup-content">
      <header className="signup-title">
        <UserRound size={25} aria-hidden="true" />
        <div>
          <h1>Crear cuenta</h1>
          <p>Completa tus datos para registrarte.</p>
        </div>
      </header>

      {success ? (
        <div className="signup-success" role="status">
          <p>{success}</p>
          <Link to="/login" className="signup-back-link">
            Ir a iniciar sesión
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="signup-form">
          <div className="signup-grid">
            {renderTextField("nombre1", "Primer nombre", { required: true, autoComplete: "given-name" })}
            {renderTextField("nombre2", "Segundo nombre", { autoComplete: "additional-name" })}
            {renderTextField("apellido1", "Primer apellido", { required: true, autoComplete: "family-name" })}
            {renderTextField("apellido2", "Segundo apellido", { required: true })}
            {renderTextField("birthDate", "Fecha de cumpleaños", { required: true, type: "date", max: today })}
            {renderTextField("email", "Correo electrónico", { required: true, type: "email", autoComplete: "email" })}
            {renderTextField("phoneNumber", "Teléfono", { required: true, type: "tel", autoComplete: "tel" })}

            <div className="signup-field">
              <label htmlFor="generated-username">Nombre de usuario</label>
              <input id="generated-username" value={username} readOnly placeholder="Se genera con tu nombre y apellido" />
              <span className="signup-field-hint">Se genera automáticamente a partir de tu primer nombre y apellido.</span>
            </div>
          </div>

          {renderPasswordField("password", "Contraseña", "new-password")}
          <ul className="signup-password-rules" aria-label="Requisitos de contraseña">
            {passwordRules.map(({ label, test }) => (
              <li className={test(form.password) ? "is-valid" : ""} key={label}>
                {label}
              </li>
            ))}
          </ul>

          {renderPasswordField("confirmPassword", "Confirmar contraseña", "new-password")}
          {form.confirmPassword && !passwordsMatch && (
            <p className="signup-inline-error">Las contraseñas no coinciden.</p>
          )}
          {error && <p className="signup-error" role="alert">{error}</p>}

          <GradientButton
            type="submit"
            disabled={isSubmitting || !username || !isPasswordValid || !passwordsMatch}
            className="signup-submit"
          >
            {isSubmitting ? "Creando cuenta..." : "Registrarme"}
          </GradientButton>

          <p className="signup-login-prompt">
            ¿Ya tienes una cuenta? <Link to="/login">Inicia sesión</Link>
          </p>
        </form>
      )}
    </section>
  );
};

export default SignUpForm;
